<script setup lang="ts">
import {
	onMounted,
	Ref,
	ref,
	watch,
	toRefs,
	shallowRef,
	computed,
	provide,
	inject,
	onBeforeUnmount,
	WritableComputedRef,
	nextTick,
} from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { useApiClient } from "@/api/client";
import MetricsDataTable from "@/modules/explorer/components/Metrics/Table/MetricsDataTable.vue";
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import ConfigureAlertDialog from "@/modules/explorer/components/alerts/ConfigureAlertDialog.vue";
import TenStackTable from "@/modules/explorer/components/Metrics/Table/TenStackTable.vue";
import { toRawDeep } from "@/Utils/to-raw-deep";
import PrimarySaveDocumentsDialog from "@/modules/explorer/components/ManageDocuments/PrimaryDialog.vue";
import OverwriteConfirmation from "@/modules/explorer/components/ManageDocuments/OverwriteConfirmation.vue";
import useDialog from "@/ui/composables/useDialog";
import { addWatcherForRealtime } from "@/modules/explorer/composables/realtime-watcher";
import { processedDocument } from "chartjs-graph";
import {
	derivedDocumentKey,
	documentKey,
} from "@/modules/explorer/ExplorerUtils";
import NewDataTableScheduleDialog from "@/modules/explorer/components/Metrics/NewDataTableScheduleDialog.vue";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { useBaseStore } from "@/stores/base";
import DocumentDO = App.Data.Explorer.DocumentDO;
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import HiddenColumnsConfigurationDialog from "@/modules/explorer/components/Metrics/HiddenColumnsConfigurationDialog.vue";
import DataViewTabs from "@/modules/explorer/components/DataViewTabs.vue";
import DownloadMenu from "@/modules/explorer/components/DownloadMenu.vue";
import {
	getCurrentDataTable,
	updateCurrentDataTable,
	hierarchyRootIsTheTotal,
	pivotRowKey,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import DataTableDO = App.Data.Explorer.DataTableDO;
import { downloadData } from "@/modules/explorer/composables/DownloadData";
import ExportFileType = App.Enums.ExportFileType;
import DataTableSql from "@/modules/explorer/components/Metrics/Table/DataTableSql.vue";
import { TableContext } from "@/modules/explorer/components/Metrics/Table/context";
import { useDataTablePrefetch } from "@/modules/explorer/composables/useDataTablePrefetch";
import {
	dataIdentityKey,
	requestIdentityKey,
} from "@/modules/explorer/composables/dataTableDisplaySettings";
import { useAbortController } from "@/modules/explorer/composables/useAbortController";
import { rememberedTotals } from "@/modules/explorer/composables/dataTableTotals";

const fetchDataPrefix: string = inject("fetchDataPrefix") ?? "/";

const { prefetch, invalidate, responseForSettings, supersetTotals } =
	useDataTablePrefetch();

const { abortController, abortRequestAndResetController } =
	useAbortController();

const useTanstack = true;

const explorerStore = useExplorerStore();
const tableContext = ref<TableContext>();
const isTenStackTableLoaded = computed(
	() => (tableContext.value?.headerHeight ?? 0) > 0
);

const unableToLoadData = ref(false);
const isLoading = ref(false);
const { lastUpdated } = toRefs(explorerStore.explorerState);
const isReorderingDataTable = ref(false);
addWatcherForRealtime(
	() => getDataTableTotals(true),
	() => fetchDataForDataTables(true)
);

const document = computed<DocumentDO>(() => explorerStore.document);
const derivedDocument = shallowRef<DocumentDO>(document.value);

provide(derivedDocumentKey, derivedDocument);
provide(documentKey, document);

watch(derivedDocument, () => {
	explorerStore.derivedDocument = derivedDocument.value;
});

let lastFetchedKey: string | null = null;

const requestsIdenticalData = (): boolean =>
	lastFetchedKey !== null &&
	requestIdentityKey(document.value) === lastFetchedKey &&
	dataIdentityKey(document.value) === lastFetchedKey;

const logDisplayOnlyChange = (): void => {
	if (fetchDataPrefix !== "explorer/") {
		return;
	}

	void useApiClient("explorer").logDocument(
		processedDocument(document.value),
		fetchDataPrefix
	);
};

const customSqlMode = computed(() => {
	return !!explorerStore.document.data_tables[0].custom_sql;
});

const hasPivotedColumn = computed(() => {
	return pivotRowKey(explorerStore.document.data_tables[0]) !== null;
});

const hasHiddenColumns = computed(() => {
	const hiddenColumns = explorerStore.document.data_tables[0].hidden_columns;
	return hiddenColumns?.length > 0;
});

const currentDataTable: WritableComputedRef<DataTableDO> | null =
	getCurrentDataTable(document.value, derivedDocument.value);
const showDownloadDataTableMenu = ref(false);
const dialog = useDialog();
const dataTableToEdit = ref<DataTableDO | null>(null);
const isManageDocumentsVisible = ref(false);
const showAlertConfigurationDialog = ref(false);
const isOverwriteDocumentVisible = ref(false);
const showHiddenColumnsDialog = ref(false);
const showCustomSqlDialog = ref(false);
const alertColumnProperties =
	ref<App.Data.Alerts.TableAlertPropertiesDTO | null>(null);

// Promise to handle document selection between save, overwrite, or none
let documentPromiseResolve:
	| ((value: "save" | "overwrite" | "none") => void)
	| null = null;
let documentPromiseReject: (() => void) | null = null;

const selection = (): Promise<"save" | "overwrite" | "none"> =>
	new Promise((resolve, reject) => {
		documentPromiseResolve = resolve;
		documentPromiseReject = reject;
	});

// Prepend the totals row when enabled. Returns a new object/array so the cached
// superset is never mutated.
const withTotalsRow = (
	response: DataTableResponseDTO,
	totals: Array<unknown> | null | undefined
): DataTableResponseDTO => {
	const table = document.value.data_tables[0];
	const shouldShowTotals =
		table.show_total_values &&
		!table.custom_sql &&
		pivotRowKey(table) === null &&
		!hierarchyRootIsTheTotal(table) &&
		totals != null &&
		Object.keys(totals).length > 0;

	if (!shouldShowTotals) {
		return response;
	}

	const rows = response.data as unknown as Record<string, string>[];
	return {
		...response,
		data: [
			{ ...totals, isTotalRow: true },
			...rows,
		] as unknown as DataTableResponseDTO["data"],
	};
};

const dataTableRows = shallowRef<DataTableResponseDTO | null>(null);
const dataTableRowsKey = ref<string | null>(null);
const totalsByKey = ref(new Map<string, Array<unknown>>());

const rememberTotals = (key: string, totals: Array<unknown> | null): void => {
	totalsByKey.value = rememberedTotals(totalsByKey.value, key, totals);
};

const totalsForRows = (): Array<unknown> | null =>
	dataTableRowsKey.value === null
		? null
		: totalsByKey.value.get(dataTableRowsKey.value) ?? null;

const dataTableResponse = computed<DataTableResponseDTO | null>(() =>
	dataTableRows.value === null
		? null
		: withTotalsRow(dataTableRows.value, totalsForRows())
);

const serveDataTableFromCache = (): boolean => {
	// Another option is to also call the prefetch() method in the addWatcherForRealtime function.
	// This would send another api call for totals + data per tick but remove the loading state when in live mode.
	if (document.value.is_updated_in_background) {
		return false;
	}

	const cached = responseForSettings(document.value);
	if (!cached) {
		return false;
	}

	const key = requestIdentityKey(document.value);

	dataTableRows.value = cached;
	dataTableRowsKey.value = key;

	if (supersetTotals.value != null) {
		rememberTotals(key, supersetTotals.value);
	}

	unableToLoadData.value = false;
	isReorderingDataTable.value = false;

	derivedDocument.value = toRawDeep<DocumentDO>(document.value);
	lastFetchedKey = key;

	logDisplayOnlyChange();

	if (totalsForRows() === null) {
		void fetchTotals();
	}

	return true;
};

const fetchTotals = async (): Promise<void> => {
	abortRequestAndResetController();
	const controller = abortController.value;
	const key = requestIdentityKey(document.value);

	const response = await useApiClient(
		"explorer"
	).fetchTotalsForDocumentDataTable(
		processedDocument(document.value),
		controller?.signal
	);

	if (controller !== abortController.value) {
		return;
	}

	rememberTotals(key, response.isOk() ? response.value : null);
};

const fetchDataForDataTables = async (inBackground = false) => {
	abortRequestAndResetController();
	const controller = abortController.value;
	const fetchedKey = requestIdentityKey(document.value);

	const response = await promptInvalidPermissions(
		useApiClient("explorer").fetchDataForDocumentDataTable(
			processedDocument(document.value),
			fetchDataPrefix,
			controller?.signal
		)
	);

	if (controller !== abortController.value) {
		return;
	}

	if (response.isOk()) {
		const apiData = response.value;
		const dataTableResponses = apiData["data"];
		dataTableRows.value = dataTableResponses[0];
		dataTableRowsKey.value = fetchedKey;
		unableToLoadData.value = false;
		derivedDocument.value = apiData["derivedDocument"];
		lastFetchedKey = fetchedKey;
	}

	if (response.isErr()) {
		unableToLoadData.value = true;
		dataTableRows.value = null;
		dataTableRowsKey.value = null;
		lastFetchedKey = null;
	}
	isReorderingDataTable.value = false;
	explorerStore.updateLoadingState(false, inBackground, isLoading);
};

const userIsAdmin = useBaseStore().currentUser.is_motives_admin;

const updateDataTable = (
	dataTable: App.Data.Explorer.DataTableDO,
	reorderingDataTable?: boolean
) => {
	if (reorderingDataTable) {
		isReorderingDataTable.value = true;
	}
	updateCurrentDataTable(document.value, derivedDocument.value, dataTable);
};

const defineAlertOnColumn = async (
	columnProperties: App.Data.Alerts.TableAlertPropertiesDTO
) => {
	alertColumnProperties.value = columnProperties;
	if (explorerStore.isSavedDocument && !explorerStore.isOpenedButUnsaved) {
		showAlertConfigurationDialog.value = true;
		return;
	}

	if (!explorerStore.isSavedDocument && !explorerStore.isOpenedButUnsaved) {
		const choice = await dialog.confirm({
			title: "Please save your document",
			message:
				"To define an alert, the document must be saved. Would you like to save it now?",
		});

		if (!choice) return;

		explorerStore.pendingAlertAfterSave = true;
		isManageDocumentsVisible.value = true;
		return;
	}

	explorerStore.pendingAlertAfterSave = true;
	isOverwriteDocumentVisible.value = true;
};

const isSaveScheduleVisible: Ref<boolean> = ref(false);
const savedDocumentUuid: Ref<string> = ref("");

watch(isSaveScheduleVisible, (isVisible) => {
	if (!isVisible) {
		savedDocumentUuid.value = "";
	}
});

watch(
	() => explorerStore.isSavedDocument,
	(isSaved) => {
		if (isSaved && explorerStore.pendingAlertAfterSave) {
			// Document was just saved and we have a pending alert
			showAlertConfigurationDialog.value = true;
			explorerStore.pendingAlertAfterSave = false;
		}
	}
);

const customSql = () => {
	dataTableToEdit.value = structuredClone(
		toRawDeep<DataTableDO>(dataTableResponse.value?.dataTableDO)
	) as DataTableDO;
	showCustomSqlDialog.value = true;
};

const unpivotTable = () => {
	dataTableToEdit.value = structuredClone(
		toRawDeep<DataTableDO>(dataTableResponse.value?.dataTableDO)
	) as DataTableDO;
	dataTableToEdit.value.pivot_dimension = null;
	updateCurrentDataTable(
		document.value,
		derivedDocument.value,
		dataTableToEdit.value
	);
};
const hideColumns = () => {
	dataTableToEdit.value = structuredClone(
		toRawDeep<DataTableDO>(dataTableResponse.value?.dataTableDO)
	) as DataTableDO;
	showHiddenColumnsDialog.value = true;
};

const scheduleExport = () => {
	if (explorerStore.isSavedDocument) {
		savedDocumentUuid.value =
			explorerStore.explorerState.lastOpenedSavedDocument.uuid;
		isSaveScheduleVisible.value = true;
	}
};

const userCanScheduleExport = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_manage_scheduler"
	);
});

const dataTableWrapper: Ref<HTMLElement | null> = ref(null);
const dataTableAlerts: Ref<HTMLElement | null> = ref(null);
const dataTableHeight: Ref<number> = ref(0);

let resizeObserver: ResizeObserver | null = null;

const updateDataTableHeight = async () => {
	// set the height to 0 so the dataTableWrapper can resize and calculate the correct height.
	dataTableHeight.value = 0;
	await nextTick();

	const computedStyle = window.getComputedStyle(dataTableWrapper.value);
	const paddingTop = parseFloat(computedStyle.paddingTop);
	const paddingBottom = parseFloat(computedStyle.paddingBottom);
	const wrapperHeight = dataTableWrapper.value.getBoundingClientRect().height;
	const alertsHeight =
		dataTableAlerts.value?.getBoundingClientRect().height ?? 0;

	dataTableHeight.value =
		wrapperHeight - paddingTop - paddingBottom - alertsHeight;
};

watch(dataTableAlerts, (newAlerts, oldAlerts) => {
	if (oldAlerts) {
		resizeObserver?.unobserve(oldAlerts);
	}
	if (newAlerts) {
		resizeObserver?.observe(newAlerts);
	}
});

const downloadDataTableEmit = async (exportFileType: ExportFileType) => {
	await downloadData(document.value, exportFileType, "data-table");
};

const getDataTableTotals = async (inBackground = false) => {
	explorerStore.updateLoadingState(true, inBackground, isLoading);

	await fetchTotals();
};

onMounted(async () => {
	await usePropertyStore().loadProperties("is_configurable_in_data_table");

	await getDataTableTotals();
	await fetchDataForDataTables();

	// The superset cache is a module-level singleton that can outlive this
	// component's previous mount. always invalidate on mount so the user gets fresh data.
	invalidate();
	void prefetch(document.value, fetchDataPrefix);

	resizeObserver = new ResizeObserver(updateDataTableHeight);

	resizeObserver.observe(window.document.body);
	if (dataTableAlerts.value) {
		resizeObserver.observe(dataTableAlerts.value);
	}
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
});

watch(lastUpdated, async () => {
	if (requestsIdenticalData()) {
		logDisplayOnlyChange();
		return;
	}

	if (serveDataTableFromCache()) {
		return;
	}

	await getDataTableTotals(isReorderingDataTable.value);
	await fetchDataForDataTables(isReorderingDataTable.value);

	void prefetch(document.value, fetchDataPrefix);
});
</script>

<template>
	<new-data-table-schedule-dialog
		v-if="isSaveScheduleVisible"
		v-model="isSaveScheduleVisible"
		:stored_document_uuid="savedDocumentUuid"
	/>

	<overwrite-confirmation
		message="In order to define an alert you must save the document."
		v-if="isOverwriteDocumentVisible"
		v-model="isOverwriteDocumentVisible"
		@saveAsNew="() => documentPromiseResolve('save')"
		@overwrite="() => documentPromiseResolve('overwrite')"
		@close="() => documentPromiseReject()"
	/>

	<primary-save-documents-dialog
		v-if="isManageDocumentsVisible"
		v-model="isManageDocumentsVisible"
		is-save-state
		@define:document="() => documentPromiseResolve('none')"
	/>

	<configure-alert-dialog
		v-if="showAlertConfigurationDialog"
		v-model="showAlertConfigurationDialog"
		:column-properties="alertColumnProperties"
	/>

	<teleport defer to="#download-button">
		<v-btn
			v-tooltip="'Export data-table as XLSX/CSV'"
			icon="fa: fa-solid fa-download"
			variant="text"
			size="small"
			@click="showDownloadDataTableMenu = true"
			color="on-surface"
		/>
	</teleport>

	<download-menu
		v-model="showDownloadDataTableMenu"
		data-type="Data Table"
		@download="downloadDataTableEmit"
	/>

	<hidden-columns-configuration-dialog
		v-if="showHiddenColumnsDialog && dataTableResponse"
		v-model="showHiddenColumnsDialog"
		:data-table-to-edit="dataTableToEdit"
		@update="updateDataTable"
		:data="dataTableResponse.data"
	/>
	<data-table-sql
		v-if="showCustomSqlDialog && dataTableResponse"
		v-model="showCustomSqlDialog"
		:data-table-to-edit="dataTableToEdit"
		@update="updateDataTable"
	/>

	<v-card class="container">
		<div class="d-flex flex-row justify-space-between pa-2 pb-4">
			<div class="d-flex align-center mr-4 pl-4 truncate-children left-flex">
				<span>Label: </span>
				<b v-if="dataTableResponse" class="ml-1">
					{{
						$t(
							`properties.${dataTableResponse?.dataTableDO.dimensions[0].name_clean}.title`
						)
					}}
				</b>

				<template v-if="dataTableResponse?.dataTableDO.dimensions.length > 1">
					<span class="ml-3"> Offsets: </span>
					<template
						v-for="(
							offset, i
						) in dataTableResponse?.dataTableDO.dimensions.slice(1)"
						:key="i"
					>
						<b v-if="i">,</b>
						<b class="ml-1">
							{{ $t(`properties.${offset?.name_clean}.title`) }}
						</b>
					</template>
				</template>

				<v-divider style="height: 100%" class="ml-2" vertical />

				<span class="ml-2">
					{{ dataTableResponse?.dataTableDO.metrics.length }}
					Metric{{
						dataTableResponse?.dataTableDO.metrics.length === 1 ? "" : "s"
					}}
				</span>
			</div>
			<div class="d-flex flex-row ga-2 right-flex">
				<Transition name="slide-fade" appear>
					<v-chip
						v-if="hasPivotedColumn"
						prepend-icon="fa-solid fa-table-pivot"
						variant="flat"
						color="secondary"
						size="small"
						style="height: 29px"
						:disabled="!dataTableResponse || hasHiddenColumns"
						@click="unpivotTable"
					>
						<span class="pl-1">Unpivot Table</span>
					</v-chip>
				</Transition>
				<Transition name="slide-fade" appear>
					<v-chip
						v-if="userIsAdmin"
						prepend-icon="fa-solid fa-database"
						variant="flat"
						color="secondary"
						size="small"
						style="height: 29px"
						:disabled="!dataTableResponse"
						@click="customSql"
					>
						<span class="pl-1">Custom SQL</span>
					</v-chip>
				</Transition>
				<Transition name="slide-fade" appear>
					<v-chip
						prepend-icon="fa-duotone fa-table-columns"
						variant="flat"
						color="secondary"
						size="small"
						style="height: 29px"
						:disabled="!dataTableResponse || customSqlMode || hasPivotedColumn"
						@click="hideColumns"
					>
						<span class="pl-1">Hide Columns</span>
					</v-chip>
				</Transition>
				<Transition name="slide-fade" appear>
					<v-chip
						prepend-icon="fa-duotone fa-calendar-clock"
						variant="flat"
						color="secondary"
						size="small"
						style="height: 29px"
						@click="scheduleExport"
						v-if="userCanScheduleExport"
						:disabled="!explorerStore.isSavedDocument"
					>
						<span class="pl-1">Schedule Export</span>
					</v-chip>
				</Transition>
				<data-view-tabs />
			</div>
		</div>

		<div class="pa-2 overflow-x-auto" ref="dataTableWrapper">
			<v-skeleton-loader
				v-show="isLoading"
				type="table-row-divider,table-row, table-row, table-row"
			/>

			<template v-if="!useTanstack">
				<metrics-data-table
					v-if="dataTableResponse !== null && !isLoading"
					v-show="!isLoading"
					:data-table="dataTableResponse"
					:data-table-index="0"
					@setColumnAlert="defineAlertOnColumn"
					v-model:sort-by="currentDataTable.sort_by"
					v-model:sort-direction="currentDataTable.sort_direction"
				/>
			</template>

			<template v-else-if="!isLoading">
				<div ref="dataTableAlerts" class="d-flex flex-column">
					<v-alert
						v-if="dataTableResponse?.hasMoreRows"
						type="warning"
						density="compact"
						closable
						class="ma-4"
					>
						More data available. To view all data use the Download CSV option.
					</v-alert>
					<v-alert
						v-if="dataTableResponse?.customSqlError"
						title="Custom Sql Error"
						type="error"
						>{{ dataTableResponse.customSqlError }}</v-alert
					>
					<v-alert
						v-if="
							!isLoading &&
							explorerStore?.explorerState.redirectToAlertsAfterSave &&
							isTenStackTableLoaded
						"
						title="Define your alert"
						type="info"
						density="compact"
						closable
					>
						Right click a metric column header to define an alert on it
					</v-alert>
				</div>
				<v-empty-state
					v-if="dataTableResponse?.data?.length === 0"
					icon="fa: fa-duotone fa-cloud-binary"
					text="No data available for the period selection and/or selected filters"
					title="No data for current configuration"
				/>

				<ten-stack-table
					v-else-if="dataTableResponse !== null && document"
					v-model="tableContext"
					:data="dataTableResponse.data"
					:data-table="dataTableResponse.dataTableDO"
					:metrics="dataTableResponse.metrics"
					:is-dashboard="false"
					:max-height="dataTableHeight"
					:main-hierarchy-label="dataTableResponse.mainHierarchyLabel"
					:period-colors="dataTableResponse.periodColors"
					:is-reordering-data-table="isReorderingDataTable"
					:print-mode="false"
					:columnsIncludedAfterCustomSql="
						dataTableResponse.columnsIncludedAfterCustomSql
					"
					:customSqlRowClasses="dataTableResponse.customSqlRowClasses"
					@update="updateDataTable"
					@setColumnAlert="defineAlertOnColumn"
					class="flex-grow"
				/>
			</template>
			<div v-else-if="unableToLoadData" style="height: 100%">
				<div class="h-100 no-data-empty-state">
					<v-empty-state
						icon="fa: fa-duotone fa-wifi-exclamation"
						text="If the issue persists, you may need to update the configuration."
						title="Unable to load data table"
					/>
				</div>
			</div>
		</div>
	</v-card>
</template>

<style lang="scss">
.truncate-children > * {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.left-flex {
	flex: 1 1 auto;
	min-width: 0;
}

.right-flex {
	flex: 0 0 auto;
}

.container {
	height: 100%;
	display: grid;
	grid-auto-columns: 1fr;
	grid-auto-rows: auto 1fr;
}

.depth-0--table-row {
	background: rgba(var(--v-theme-primary), 0.05) !important;
}

.depth-1--table-row > td.metric-horizontal {
	padding-left: 32px !important;
}

.depth-0,
.depth-1--horizontal,
.depth-1--offset {
	font-weight: bold;
}

.metric-column {
	flex: 1;
	text-align: right;
}

.customTableStyle,
.metrics-data-table {
	.dataTable {
		font-size: 0.75rem !important;
		font-weight: 400;
		line-height: 1.667;
		letter-spacing: 0.0333333333em !important;
		font-family: "Roboto", sans-serif;
	}

	table {
		border-collapse: collapse !important;
	}

	&--metric {
		text-align: right;
	}
}

.slide-fade-enter-active {
	transition: all 0.5s ease;
}

.slide-fade-leave-active {
	transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateX(20px);
	opacity: 0;
}
</style>
